import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import i18n from "../i18n";
import { translateManyClient } from "../../../utils/ItClient";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/compliance`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken(); // Get token from cookies or storage
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

export const regulatoryComplianceSlice = createApi({
  reducerPath: "regulatoryComplianceSlice",
  baseQuery: baseQuery,
  tagTypes: ["regulatoryCompliance"],
  endpoints: (builder) => ({
    // GET regulatory Compliance List
    getRegulatoryCompliance: builder.query({
      query: ({
        page,
        limit,
        search,
        endPoint = "",
        method = "Get",
        role,
        status,
      }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);
        if (role) params.append("role", role);
        if (status) params.append("status", status);

        const queryString = params.toString();
        const url = queryString ? `${endPoint}?${queryString}` : endPoint;

        return {
          url,
          method, // Ensure it's uppercase: GET, POST, etc.
        };
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const records = data?.data?.compliances ?? [];
          if (!Array.isArray(records) || records.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const compliance of records) {
            if (compliance.compliance_status)
              bucket.push(compliance.compliance_status);
            if (compliance.description)
              bucket.push(compliance.description);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? map.get(s) ?? s : s);

          dispatch(
            regulatoryComplianceSlice.util.updateQueryData(
              "getRegulatoryCompliance",
              arg,
              (draft) => {
                const arr = draft?.data?.compliances ?? [];
                arr.forEach((compliance) => {
                  compliance.compliance_status = pick(
                    compliance.compliance_status
                  );
                  compliance.description = pick(compliance.description);
                });
              }
            )
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["regulatoryCompliance"],
    }),
    getRegulatoryComplianceDetails: builder.query({
      query: (id) => `${id}`,
      providesTags: ["regulatoryCompliance"],
    }),
    // Create regulatoryCompliance
    createRegulatoryCompliance: builder.mutation({
      query: (data) => ({
        url: "",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["regulatoryCompliance"],
    }),
    // Update Seat Availability
    updateRegulatoryCompliance: builder.mutation({
      query: ({ data, id }) => ({
        url: `${id}`,
        method: "Put",
        body: data,
      }),
      invalidatesTags: ["regulatoryCompliance"],
    }),
    // DELETE a Seat Availability
    deleteRegulatoryCompliance: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "Delete",
      }),
      invalidatesTags: ["regulatoryCompliance"],
    }),
  }),
});

export const {
  useGetRegulatoryComplianceQuery,
  useGetRegulatoryComplianceDetailsQuery,
  useCreateRegulatoryComplianceMutation, // old use
  useUpdateRegulatoryComplianceMutation,
  useDeleteRegulatoryComplianceMutation,
} = regulatoryComplianceSlice;
